import React from 'react';

import { Card, Result, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { Contact } from 'models/Contact';

import { useThemeLayoutConfig } from '../../../config/ThemeDashboardLayoutConfig';
import appI18n from '../../../config/i18n';
import { ContactCard } from './ContactCard';

import './ContactGrid.scss';

interface ContactGridProps {
  contacts: Contact[];
  loading?: boolean;
}

const LoadingSkeleton: React.FC = () => {
  const skeletonCards = Array(3).fill(null);

  return (
    <>
      {skeletonCards.map((_, index) => (
        <Card key={index} className="contact-card-skeleton">
          <div className="skeleton-content">
            <div className="skeleton-avatar">
              <Skeleton.Avatar active size={100} />
            </div>
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
        </Card>
      ))}
    </>
  );
};

export const ContactGrid: React.FC<ContactGridProps> = ({
  contacts,
  loading = false,
}) => {
  const { theme } = useThemeLayoutConfig();
  const { t } = useTranslation(undefined, { i18n: appI18n });

  const styles = {
    emptyState: {
      '--contact-primary-color': theme?.token?.colorPrimary,
      '--contact-bg-container': theme?.token?.colorBgContainer,
      '--contact-text-primary': theme?.token?.colorTextHeading,
      '--contact-text-secondary': theme?.token?.colorTextSecondary,
      '--contact-border-radius': `${theme?.token?.borderRadiusLG}px`,
    } as React.CSSProperties,
  };

  if (loading) {
    return (
      <div className="contact-grid">
        <LoadingSkeleton />
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="contact-grid">
        <div className="empty-state" style={styles.emptyState}>
          <Result
            status="404"
            title={t('scenes.contact_us.components.contact_grid.empty.title')}
            subTitle={t(
              'scenes.contact_us.components.contact_grid.empty.subtitle'
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="contact-grid">
      {contacts.map((contact) => (
        <ContactCard key={contact.id} contact={contact} />
      ))}
    </div>
  );
};
